import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight, ScanText } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PremiumGate } from '@/components/plans/plan-notice'
import { JumpNav, ToolPage } from '@/prakrit/components/grammar/tools/tool-page'
import { KarakParser } from '@/prakrit/components/grammar/tools/karak-parser'
import { KarakPractice } from '@/prakrit/components/grammar/tools/karak-practice'

const KarakPage: FC = (): ReactNode => (
    <Layout>
        <Seo
            title='Sentence parser'
            description='कारक: tap a Prakrit sentence to see the role of every word (क्रिया, कर्ता, कर्म, अव्यय), its case or tense, the eight कारक, and why word order is free.'
        />
        <PremiumGate kind='grammar'>
            <ToolPage
                current='/grammar/karak'
                eyebrow='कारक · वाक्य विश्लेषण'
                title={{ hi: 'कारक और वाक्य विश्लेषण', en: 'Parse a sentence: who does what to whom' }}
                intro={{
                    hi: 'एक उदाहरण चुनिए या अपना वाक्य लिखिए। हर शब्द की भूमिका (क्रिया, कर्ता, कर्म, अव्यय), उसकी विभक्ति या काल, मूल शब्द और अर्थ देखिए। कोई नियम टूटे तो कारण हिन्दी और अंग्रेज़ी में मिलेगा।',
                    en: 'Pick an example or type your own sentence. See the role of every word (verb, subject, object, indeclinable), its case or tense, its head word and meaning. If a rule fails, the reason is given in Hindi and English.'
                }}
                kidsIntro={{
                    hi: 'एक वाक्य चुनो और देखो: काम कौन कर रहा है, और किस पर!',
                    en: 'Pick a sentence and see who is doing the action, and to what!'
                }}
                source='नि.९–१४, २०–५९'
                headerExtra={(
                    <div className='flex flex-col gap-4'>
                        <JumpNav
                            links={[
                                { id: 'karak-pick', hi: 'वाक्य विश्लेषण', en: 'Parse' },
                                { id: 'karak-table-title', hi: 'आठ कारक', en: 'The eight cases' },
                                { id: 'karak-practice', hi: 'अभ्यास', en: 'Practice' }
                            ]}
                        />
                        <Link
                            href='/grammar/vakya'
                            className='group flex max-w-3xl items-center gap-4 rounded-2xl bg-foreground px-5 py-4 text-background shadow-card transition-colors hover:bg-foreground/90'
                        >
                            <ScanText className='size-6 shrink-0 text-saffron' aria-hidden='true' />
                            <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
                                <span className='flex flex-wrap items-baseline gap-x-2'>
                                    <span lang='hi' className='literary text-lg'>वाक्यविश्लेषणम्</span>
                                    <span lang='en' className='font-semibold'>Sentence analyser</span>
                                </span>
                                <span className='text-sm text-background/75'>
                                    <span lang='hi' data-ex='hi' className='hindi'>कोई भी वाक्य लिखिए: हर शब्द का व्याकरण, उसकी भूमिका और कर्ता-क्रिया का मेल।</span>
                                    <span aria-hidden='true' className='ex-sep'> · </span>
                                    <span lang='en' data-ex='en'>Type any sentence: every word with its grammar and role, and a subject–verb agreement check.</span>
                                </span>
                            </span>
                            <ArrowRight className='size-5 shrink-0 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                        </Link>
                    </div>
                )}
            >
                <KarakParser />
                <KarakPractice />
            </ToolPage>
        </PremiumGate>
    </Layout>
)

export default KarakPage
